/*
 * UNIFIED COMPONENT FACTORY
 * 
 * Single factory that uses ComponentFactory from @flexo/general.
 * Includes all components for all apps - simplified and unified approach.
 */

import React from 'react';
import { 
  ComponentFactory, 
  createComponentRegistry, 
  ComponentDependencies,
  BaseWidgetConfig 
} from '@flexo/general';

// Import all components
import {
  DetailsList,
} from '@flexo/molecules';
import {
  Footer,
  StatsOverview,
  HorizontalWrapper,
  BatteryChart,
  CommunityStats,
  GraphContainer,
  Header,
  HeaderWeb,
  RegisterOOBcode,
  AccountCreationOOBcode,
  AccountCreationWeb,
  InformationCard,
} from '@flexo/organisms';
import {
  GenericTable,
  CommunitiesTable,
  MembersTable,
  PaymentsTable,
  SitePaymentsTable,
  EsriMapComponent,
} from '@flexo/atoms';
import { PasswordResetWeb, ForgotPasswordWeb, ForgotPasswordMobile } from '@flexo/templates';

// Import local components
import WebLogIn from './web-log-in/web-log-in';
import ApiLogIn from './mobile-log-in/mobile-log-in';
import { RegisterByInvite } from './register-by-invite/register-by-invite';

// ================================
// UNIFIED COMPONENT DEPENDENCIES
// ================================
const unifiedComponentDependencies: ComponentDependencies = {
  // From @flexo/molecules
  DetailsList,
  
  // From @flexo/organisms
  Footer,
  StatsOverview,
  HorizontalWrapper,
  BatteryChart,
  CommunityStats,
  GraphContainer,
  Header,
  HeaderWeb,
  RegisterOOBcode,
  AccountCreationOOBcode,
  AccountCreationWeb,
  InformationCard,
  
  // From @flexo/atoms
  GenericTable,
  CommunitiesTable,
  MembersTable,
  PaymentsTable,
  SitePaymentsTable,
  EsriMapComponent,
  
  // From @flexo/templates
  PasswordResetWeb,
  ForgotPasswordWeb,
  ForgotPasswordMobile,
  
  // Local components
  WebLogIn,
  ApiLogIn,
  RegisterByInvite,
};

// ================================
// FACTORY INSTANCE
// ================================
let factoryInstance: ComponentFactory | null = null;

const getFactory = (): ComponentFactory => {
  if (!factoryInstance) {
    const registry = createComponentRegistry(unifiedComponentDependencies);
    factoryInstance = new ComponentFactory(registry);
  }
  return factoryInstance;
};

// ================================
// HELPER FUNCTIONS
// ================================

/**
 * Check if the props contain a configuration object (with rows)
 */
const isConfiguration = (props: any): boolean => {
  return props && typeof props === 'object' && 'configuration' in props && props.configuration?.rows;
};

/**
 * Check if the props contain a single widget config
 */
const isWidgetConfig = (props: any): boolean => {
  return props && typeof props === 'object' && 'type' in props && 'id' in props;
};

// ================================
// UNIFIED PUBLIC API
// ================================

/**
 * UNIFIED FACTORY FUNCTION
 * 
 * Main factory function that handles both:
 * 1. Single widget: Factory(widgetConfig, styles)
 * 2. Configuration: Factory({ configuration: configObject })
 */
export function Factory(
  props: BaseWidgetConfig | { configuration?: any } | null = null,
  additionalStyles: React.CSSProperties = {}
): React.ReactElement {
  const factory = getFactory();
  
  // Handle configuration object
  if (props && 'configuration' in props) {
    return factory.createFromConfiguration(props.configuration);
  }
  
  // Handle single widget with additional styles
  if (props && 'type' in props && 'id' in props) {
    return factory.create(props, additionalStyles);
  }
  
  // Handle null/empty
  return <div>Empty factory configuration</div>;
}

/**
 * UNIFIED FACTORY COMPONENT
 * 
 * React component that handles both:
 * 1. Single widget: <FactoryComponent {...widgetConfig} />
 * 2. Configuration: <FactoryComponent configuration={configObject} />
 */
export const FactoryComponent: React.FC<any> = (props: any) => {
  const factory = getFactory();
  
  // Handle configuration-style usage: <FactoryComponent configuration={...} />
  if (isConfiguration(props)) {
    return factory.createFromConfiguration(props.configuration);
  }
  
  // Handle widget-style usage: <FactoryComponent {...widgetConfig} />
  if (isWidgetConfig(props)) {
    return factory.create(props);
  }
  
  // Handle empty or invalid props
  return <div>Invalid factory props</div>;
};

// ================================
// COMPATIBILITY EXPORTS
// ================================

// Memoized versions
export const MemoizedFactoryComponent = React.memo(FactoryComponent);
export const CommunityFactoryComponent = React.memo(FactoryComponent);

// Legacy aliases (all point to the same unified implementations)
export const MobileFactory = Factory;
export const AdminFactory = Factory;
export const MobileFactoryComponent = FactoryComponent;
export const AdminFactoryComponent = FactoryComponent;
export const MemoizedMobileFactoryComponent = React.memo(FactoryComponent);
export const MemoizedAdminFactoryComponent = React.memo(FactoryComponent);

// Export factory type for debugging
export const getFactoryType = () => 'unified';

/*
 * UNIFIED USAGE EXAMPLES:
 * 
 * // 1. Single widget (function style) - EXISTING USAGE WORKS
 * const element = Factory({
 *   id: 'my-widget',
 *   type: 'card',
 *   data: { title: 'My Card' }
 * });
 * 
 * // 2. Single widget (component style) - NEW OPTION
 * <FactoryComponent id="my-widget" type="card" data={{ title: 'My Card' }} />
 * 
 * // 3. Configuration (component style) - NEW OPTION
 * <FactoryComponent configuration={{
 *   rows: [
 *     {
 *       id: 'row-1',
 *       widgets: [
 *         { id: 'header-1', type: 'header', data: { title: 'Dashboard' } }
 *       ]
 *     }
 *   ]
 * }} />
 * 
 * // 4. Configuration (function style) - NEW OPTION  
 * const element = Factory({ configuration: configObject });
 */ 